import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
} from 'react-native';

import {
  isSiteInformationPersistedProps,
  type SiteInformationProps,
  type SiteInformationPersistedProps,
  type SiteInformationInitialProps,
} from './useSiteInformation';
import {
  FormFieldGroup,
  FormTwoOptionChoice,
  FormRow,
  FormColumn,
  UnitNumberField,
  FormInfoHelpModal,
  FormFieldLabelWithInfo,
  StepForm,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  FormFieldShowInfoIconsProvider,
} from '../../../components/forms';
import { Eye, EyeOff } from '../../../components/icons';
import { PermissionAwareField } from '../../../components/PermissionAwareField';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import {
  StepShell,
  stepHeaderVariantForPlatform,
} from '../../../components/StepShell';
import { RAISED_FLOORING_YES_NO } from '../../../constants/dataCenterFlooringOptions';
import {
  SITE_DIMENSIONS_SUBSECTION_FIELDS,
  SITE_INFORMATION_NOTES_SUBSECTION_FIELDS,
} from '../../../constants/requestFieldDescriptors';
import { SECTION_IDS } from '../../../constants/sectionIds';
import {
  SITE_INFO_STEP_FIELD_INFO,
  type SiteInfoStepInfoKey,
  siteInfoStepInfoA11y,
} from '../../../constants/siteInfoStepFieldInfo';
import { useFormFieldVisibilityContext } from '../../../hooks/useFormFieldVisibilityContext';
import {
  colors,
  spacing,
  fontSize,
  lineHeight,
  fontWeight,
} from '../../../theme';
import { subsectionHeaderMetricsProps } from '../../../utils/subsectionHeaderMetrics';
import { RequestFormAdditionalInformation } from '../subsections/AdditionalInformation';

const SiteInformationInitialLayout: React.FC<SiteInformationInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  showFullDetails = true,
}) => {
  const [infoKey, setInfoKey] = useState<SiteInfoStepInfoKey | null>(null);

  const siteDimensionsId = SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL;

  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage,
    requestId,
  });

  const siteDimCardAttachments = attachmentsBySection[siteDimensionsId] || [];
  const siteDimCardSelected = selectedFiles[siteDimensionsId] || [];

  /** Site address is captured on Site Walk; no duplicate card here. */
  const siteInformationAddressCard = null;

  const dimensionsFormFields = (
    <>
      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteDataCenterFlooring"
            requestStage={requestStage}
            requestId={requestId}
          >
            <FormFieldGroup
              style={[
                formFieldInStack,
                siteInformationInitialStyles.raisedFloorChoiceRow,
              ]}
            >
              <FormFieldLabelWithInfo
                inlineWithControl
                onInfo={() => setInfoKey('raisedFloorStorage')}
                infoA11yHint={siteInfoStepInfoA11y('raisedFloorStorage').hint}
                a11yDescription={
                  siteInfoStepInfoA11y('raisedFloorStorage').description
                }
              >
                Data center flooring (raised floor)?
              </FormFieldLabelWithInfo>
              <FormTwoOptionChoice
                label="Data center flooring (raised floor)?"
                hideLabel
                value={String(formData.siteDataCenterFlooring ?? '')}
                options={RAISED_FLOORING_YES_NO}
                onSelect={v => updateFormData('siteDataCenterFlooring', v)}
                disabled={disabled}
                readOnlyMode={disabled}
                testID="form-flooring-siteDataCenterFlooring"
                containerStyle={siteInformationInitialStyles.raisedFloorRadios}
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteWeightSupportOfFloor"
            requestStage={requestStage}
            requestId={requestId}
          >
            <FormFieldGroup style={formFieldInStack}>
              <UnitNumberField
              fieldKey="siteWeightSupportOfFloor"
                pair="floorWeight"
                label="Weight support of floor"
                renderLabel={text => (
                  <FormFieldLabelWithInfo
                    onInfo={() => setInfoKey('floorLoad')}
                    infoA11yHint={siteInfoStepInfoA11y('floorLoad').hint}
                    a11yDescription={
                      siteInfoStepInfoA11y('floorLoad').description
                    }
                  >
                    {text}
                  </FormFieldLabelWithInfo>
                )}
                value={String(formData.siteWeightSupportOfFloor ?? '')}
                onChangeText={value =>
                  updateFormData('siteWeightSupportOfFloor', value)
                }
                placeholder="Enter floor weight capacity in lbs/sqft"
                disabled={disabled}
                readOnlyMode={disabled}
                min={0}
                showStepper={false}
                containerStyle={formFieldInStack}
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRaisedFloorHeight"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteRaisedFloorHeight"
              pair="lengthIn"
              label="Height of raised floor"
              value={String(formData.siteRaisedFloorHeight ?? '')}
              onChangeText={value =>
                updateFormData('siteRaisedFloorHeight', value)
              }
              placeholder="Enter raised floor height"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteCeilingHeight"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteCeilingHeight"
              pair="lengthFt"
              label="Ceiling height"
              value={String(formData.siteCeilingHeight ?? '')}
              onChangeText={value => updateFormData('siteCeilingHeight', value)}
              placeholder="Enter ceiling height in feet"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteDroppedCeilingHeight"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteDroppedCeilingHeight"
              pair="lengthFt"
              label="Dropped ceiling height"
              value={String(formData.siteDroppedCeilingHeight ?? '')}
              onChangeText={value =>
                updateFormData('siteDroppedCeilingHeight', value)
              }
              placeholder="Enter dropped ceiling height in feet"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="sitePlenumHeight"
            requestStage={requestStage}
            requestId={requestId}
          >
            <FormFieldGroup style={formFieldInStack}>
              <UnitNumberField
              fieldKey="sitePlenumHeight"
                pair="lengthFt"
                label="If dropped ceiling, plenum height"
                renderLabel={text => (
                  <FormFieldLabelWithInfo
                    onInfo={() => setInfoKey('plenumHeight')}
                    infoA11yHint={siteInfoStepInfoA11y('plenumHeight').hint}
                    a11yDescription={
                      siteInfoStepInfoA11y('plenumHeight').description
                    }
                  >
                    {text}
                  </FormFieldLabelWithInfo>
                )}
                value={String(formData.sitePlenumHeight ?? '')}
                onChangeText={value =>
                  updateFormData('sitePlenumHeight', value)
                }
                placeholder="Enter plenum height if applicable"
                disabled={disabled}
                readOnlyMode={disabled}
                min={0}
                showStepper={false}
                containerStyle={formFieldInStack}
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteSlabToDroppedCeilingHeight"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteSlabToDroppedCeilingHeight"
              pair="lengthFt"
              label="Slab to dropped ceiling height"
              value={String(formData.siteSlabToDroppedCeilingHeight ?? '')}
              onChangeText={value =>
                updateFormData('siteSlabToDroppedCeilingHeight', value)
              }
              placeholder="Enter slab to dropped ceiling height in feet"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRoomLengthFtIn"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteRoomLengthFtIn"
              pair="lengthFt"
              label="Room Length"
              value={String(formData.siteRoomLengthFtIn ?? '')}
              onChangeText={value =>
                updateFormData('siteRoomLengthFtIn', value)
              }
              placeholder="Enter room length in feet"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRoomWidthSqftSqm"
            requestStage={requestStage}
            requestId={requestId}
          >
            <UnitNumberField
              fieldKey="siteRoomWidthSqftSqm"
              pair="lengthFt"
              label="Room Width"
              value={String(formData.siteRoomWidthSqftSqm ?? '')}
              onChangeText={value =>
                updateFormData('siteRoomWidthSqftSqm', value)
              }
              placeholder="Enter room width in feet"
              disabled={disabled}
              readOnlyMode={disabled}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>
    </>
  );

  const siteDimensionsMetrics = subsectionHeaderMetricsProps(
    SITE_DIMENSIONS_SUBSECTION_FIELDS,
    formData as Record<string, unknown>,
    fieldVisibility,
  );

  const siteDimensionsBody = (
    <StepForm
      title="Site Dimensions"
      titleSubtitle="Raised floor, ceiling and room size, and notes. Attach floor plans or photos in this section if helpful."
      {...siteDimensionsMetrics}
      containerStyle={siteInformationInitialStyles.opportunityFormSection}
      sectionId={siteDimensionsId}
      requestId={requestId}
      requestStage={requestStage}
      allowAttachments
      attachments={siteDimCardAttachments}
      onAttachFiles={
        onAttachFiles ? () => onAttachFiles(siteDimensionsId) : undefined
      }
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing || !disabled}
      disabled={disabled}
      selectedFiles={siteDimCardSelected}
      onFilesSelected={
        onFilesSelected
          ? files => onFilesSelected(siteDimensionsId, files)
          : undefined
      }
      isOffline={offlineMode}
      inlineFieldHtml={String(formData.siteAdditionalSizeInformation ?? '')}
    >
      {dimensionsFormFields}
    </StepForm>
  );

  return (
    <StepShell
      title="Site Information"
      testID="create-form-header-site-information"
      headerVariant={stepHeaderVariantForPlatform()}
    >
      <FormFieldShowInfoIconsProvider showIcons={false}>
        <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
          {siteInformationAddressCard}
          {siteDimensionsBody}
          <RequestFormAdditionalInformation
            shell="stepForm"
            titleSubtitle="Additional notes about site dimensions not captured above."
            containerStyle={siteInformationInitialStyles.opportunityFormSection}
            sectionId={siteDimensionsId}
            requestId={requestId}
            requestStage={requestStage}
            allowAttachments
            attachments={siteDimCardAttachments}
            onAttachFiles={
              onAttachFiles ? () => onAttachFiles(siteDimensionsId) : undefined
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={isEditing || !disabled}
            disabled={disabled}
            selectedFiles={siteDimCardSelected}
            onFilesSelected={
              onFilesSelected
                ? files => onFilesSelected(siteDimensionsId, files)
                : undefined
            }
            isOffline={offlineMode}
            fieldId="siteAdditionalSizeInformation"
            permissionRequestStage={requestStage}
            inputLabel="Additional Site Dimensions Information"
            placeholder="Enter additional information"
            value={String(formData.siteAdditionalSizeInformation ?? '')}
            onChangeText={value =>
              updateFormData('siteAdditionalSizeInformation', value)
            }
            webMultilineAutoSize
            maxLength={10000}
            inputUseEditableProp={false}
            inputDisabled={disabled}
            wrapFieldInRowColumn={false}
            subsectionFields={SITE_INFORMATION_NOTES_SUBSECTION_FIELDS}
            headerValues={formData as Record<string, unknown>}
          />
          <FormInfoHelpModal
            visible={infoKey !== null}
            onClose={() => setInfoKey(null)}
            title={
              infoKey != null ? SITE_INFO_STEP_FIELD_INFO[infoKey].title : ''
            }
            bullets={
              infoKey != null
                ? [...SITE_INFO_STEP_FIELD_INFO[infoKey].bullets]
                : []
            }
          />
        </FormStackColumnsOnWebProvider>
      </FormFieldShowInfoIconsProvider>
    </StepShell>
  );
};

const isWeb = Platform.OS === 'web';

const SiteInformationPersistedLayout: React.FC<
  SiteInformationPersistedProps
> = ({
  request,
  isEditing,
  editedData,
  updateEditedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
}) => {
  const [infoKey, setInfoKey] = useState<SiteInfoStepInfoKey | null>(null);
  const requestStage = String(
    request.sysRequestStage ?? request.stage ?? 'DRAFT',
  );
  const siteDimId = SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL;
  const siteDimAttachments = attachmentsBySection[siteDimId] || [];
  const siteDimSelected = selectedFiles[siteDimId] || [];

  const get = (k: string): string => {
    if (isEditing) {
      const e = editedData[k];
      if (e !== undefined && e !== null) return String(e);
    }
    const v = (request as unknown as Record<string, unknown>)[k];
    return v !== undefined && v !== null ? String(v) : '';
  };

  const setStr = (field: string, v: string) => {
    if (isEditing) updateEditedData(field, v);
  };

  const dimensionsFormFields = (
    <>
      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteDataCenterFlooring"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormFieldGroup
              style={[
                formFieldInStack,
                siteInformationPersistedStyles.raisedFloorChoiceRow,
              ]}
            >
              <FormFieldLabelWithInfo
                inlineWithControl
                onInfo={() => setInfoKey('raisedFloorStorage')}
                infoA11yHint={siteInfoStepInfoA11y('raisedFloorStorage').hint}
                a11yDescription={
                  siteInfoStepInfoA11y('raisedFloorStorage').description
                }
              >
                Data center flooring (raised floor)?
              </FormFieldLabelWithInfo>
              <FormTwoOptionChoice
                label="Data center flooring (raised floor)?"
                hideLabel
                value={String(
                  isEditing
                    ? editedData.siteDataCenterFlooring ??
                        (
                          request as unknown as {
                            siteDataCenterFlooring?: string;
                          }
                        ).siteDataCenterFlooring ??
                        ''
                    : (
                        request as unknown as {
                          siteDataCenterFlooring?: string;
                        }
                      ).siteDataCenterFlooring || '',
                )}
                options={RAISED_FLOORING_YES_NO}
                onSelect={
                  isEditing
                    ? value => setStr('siteDataCenterFlooring', value)
                    : () => {}
                }
                disabled={!isEditing}
                readOnlyMode={!isEditing}
                testID="form-flooring-siteDataCenterFlooring"
                containerStyle={
                  siteInformationPersistedStyles.raisedFloorRadios
                }
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteWeightSupportOfFloor"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormFieldGroup style={formFieldInStack}>
              <UnitNumberField
              fieldKey="siteWeightSupportOfFloor"
                pair="floorWeight"
                label="Weight support of floor"
                renderLabel={text => (
                  <FormFieldLabelWithInfo
                    onInfo={() => setInfoKey('floorLoad')}
                    infoA11yHint={siteInfoStepInfoA11y('floorLoad').hint}
                    a11yDescription={
                      siteInfoStepInfoA11y('floorLoad').description
                    }
                  >
                    {text}
                  </FormFieldLabelWithInfo>
                )}
                value={get('siteWeightSupportOfFloor')}
                onChangeText={
                  isEditing
                    ? value => setStr('siteWeightSupportOfFloor', value)
                    : () => {}
                }
                placeholder="Enter floor weight capacity in lbs/sqft"
                disabled={!isEditing}
                readOnlyMode={!isEditing}
                min={0}
                showStepper={false}
                containerStyle={formFieldInStack}
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRaisedFloorHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteRaisedFloorHeight"
              pair="lengthIn"
              label="Height of raised floor"
              value={get('siteRaisedFloorHeight')}
              onChangeText={
                isEditing
                  ? value => setStr('siteRaisedFloorHeight', value)
                  : () => {}
              }
              placeholder="Enter raised floor height"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteCeilingHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteCeilingHeight"
              pair="lengthFt"
              label="Ceiling height"
              value={get('siteCeilingHeight')}
              onChangeText={
                isEditing
                  ? value => setStr('siteCeilingHeight', value)
                  : () => {}
              }
              placeholder="Enter ceiling height in feet"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteDroppedCeilingHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteDroppedCeilingHeight"
              pair="lengthFt"
              label="Dropped ceiling height"
              value={get('siteDroppedCeilingHeight')}
              onChangeText={
                isEditing
                  ? value => setStr('siteDroppedCeilingHeight', value)
                  : () => {}
              }
              placeholder="Enter dropped ceiling height in feet"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="sitePlenumHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <FormFieldGroup style={formFieldInStack}>
              <UnitNumberField
              fieldKey="sitePlenumHeight"
                pair="lengthFt"
                label="If dropped ceiling, plenum height"
                renderLabel={text => (
                  <FormFieldLabelWithInfo
                    onInfo={() => setInfoKey('plenumHeight')}
                    infoA11yHint={siteInfoStepInfoA11y('plenumHeight').hint}
                    a11yDescription={
                      siteInfoStepInfoA11y('plenumHeight').description
                    }
                  >
                    {text}
                  </FormFieldLabelWithInfo>
                )}
                value={get('sitePlenumHeight')}
                onChangeText={
                  isEditing
                    ? value => setStr('sitePlenumHeight', value)
                    : () => {}
                }
                placeholder="Enter plenum height if applicable"
                disabled={!isEditing}
                readOnlyMode={!isEditing}
                min={0}
                showStepper={false}
                containerStyle={formFieldInStack}
              />
            </FormFieldGroup>
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteSlabToDroppedCeilingHeight"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteSlabToDroppedCeilingHeight"
              pair="lengthFt"
              label="Slab to dropped ceiling height"
              value={get('siteSlabToDroppedCeilingHeight')}
              onChangeText={
                isEditing
                  ? value => setStr('siteSlabToDroppedCeilingHeight', value)
                  : () => {}
              }
              placeholder="Enter slab to dropped ceiling height in feet"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRoomLengthFtIn"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteRoomLengthFtIn"
              pair="lengthFt"
              label="Room Length"
              value={get('siteRoomLengthFtIn')}
              onChangeText={
                isEditing
                  ? value => setStr('siteRoomLengthFtIn', value)
                  : () => {}
              }
              placeholder="Enter room length in feet"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>

      <FormRow>
        <FormColumn>
          <PermissionAwareField
            fieldId="siteRoomWidthSqftSqm"
            requestStage={request.stage || request.sysRequestStage}
            requestId={request.id}
            showFullDetails={showFullDetails}
          >
            <UnitNumberField
              fieldKey="siteRoomWidthSqftSqm"
              pair="lengthFt"
              label="Room Width"
              value={get('siteRoomWidthSqftSqm')}
              onChangeText={
                isEditing
                  ? value => setStr('siteRoomWidthSqftSqm', value)
                  : () => {}
              }
              placeholder="Enter room width in feet"
              disabled={!isEditing}
              readOnlyMode={!isEditing}
              min={0}
              showStepper={false}
              containerStyle={formFieldInStack}
            />
          </PermissionAwareField>
        </FormColumn>
      </FormRow>
    </>
  );

  /** Site address lives on Site Walk — no duplicate card on this step. */
  const siteInfoAddressCard = null;

  /**
   * Merge unsaved `editedData` over `request` so the header field-fill count and dynamic
   * subtitle update live as the user types — matches the SmallUps / Cooling persisted
   * layouts. Inline recompute (no `useMemo`) — the field set is small and there is no
   * conditional early return to break hooks order.
   */
  const headerValues: Record<string, unknown> = {
    ...(request as unknown as Record<string, unknown>),
    ...(isEditing ? editedData : {}),
  };
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: String(request.sysRequestStage ?? request.stage ?? 'DRAFT'),
    requestId: request.id,
  });
  const siteDimensionsMetrics = subsectionHeaderMetricsProps(
    SITE_DIMENSIONS_SUBSECTION_FIELDS,
    headerValues,
    fieldVisibility,
  );
  const notesHtml = isEditing
    ? String(
        editedData.siteAdditionalSizeInformation ??
          (request as Record<string, unknown>).siteAdditionalSizeInformation ??
          '',
      )
    : String(
        (request as Record<string, unknown>).siteAdditionalSizeInformation ??
          '',
      );

  const siteDimensionsBody = (
    <StepForm
      title="Site Dimensions"
      titleSubtitle="Raised floor, ceiling and room size, and notes. Attach floor plans or photos in this section if helpful."
      {...siteDimensionsMetrics}
      containerStyle={siteInformationPersistedStyles.opportunityFormSection}
      sectionId={siteDimId}
      requestId={request.id}
      requestStage={requestStage}
      allowAttachments
      attachments={siteDimAttachments}
      inlineFieldHtml={notesHtml}
      onAttachFiles={onAttachFiles ? () => onAttachFiles(siteDimId) : undefined}
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing}
      disabled={!isEditing}
      readOnlyMode={!isEditing}
      selectedFiles={siteDimSelected}
      onFilesSelected={
        onFilesSelected ? files => onFilesSelected(siteDimId, files) : undefined
      }
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
    >
      {dimensionsFormFields}
    </StepForm>
  );

  return (
    <View
      style={[
        siteInformationPersistedStyles.container,
        isWeb && siteInformationPersistedStyles.rootWeb,
      ]}
    >
      <PersistedStepHeader
        title="Site Information"
        titleTestID="site-information-step-title"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={siteInformationPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={siteInformationPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />
      <FormFieldShowInfoIconsProvider showIcons={false}>
        <FormStackColumnsOnWebProvider enabled={isWeb}>
          {siteInfoAddressCard}
          {siteDimensionsBody}
          <RequestFormAdditionalInformation
            shell="stepForm"
            withFieldStack={false}
            titleSubtitle="Additional notes about site dimensions not captured above."
            containerStyle={
              siteInformationPersistedStyles.opportunityFormSection
            }
            sectionId={siteDimId}
            requestId={request.id}
            requestStage={requestStage}
            allowAttachments
            attachments={siteDimAttachments}
            onAttachFiles={
              onAttachFiles ? () => onAttachFiles(siteDimId) : undefined
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={isEditing}
            disabled={!isEditing}
            readOnlyMode={!isEditing}
            selectedFiles={siteDimSelected}
            onFilesSelected={
              onFilesSelected
                ? files => onFilesSelected(siteDimId, files)
                : undefined
            }
            isOffline={offlineMode}
            onCaptionChange={onCaptionChange}
            pendingCaptionEdits={pendingCaptionEdits}
            fieldId="siteAdditionalSizeInformation"
            permissionRequestStage={String(
              request.stage ?? request.sysRequestStage ?? '',
            )}
            showFullDetails={showFullDetails}
            inputLabel="Additional Site Dimensions Information"
            placeholder="Enter additional information"
            value={(() => {
              const r = request as Record<string, unknown>;
              return isEditing
                ? String(
                    editedData.siteAdditionalSizeInformation ??
                      r.siteAdditionalSizeInformation ??
                      '',
                  )
                : String(r.siteAdditionalSizeInformation ?? '');
            })()}
            onChangeText={
              isEditing
                ? value => setStr('siteAdditionalSizeInformation', value)
                : () => {}
            }
            webMultilineAutoSize
            maxLength={10000}
            inputUseEditableProp
            inputEditable={isEditing}
            wrapFieldInRowColumn={false}
            subsectionFields={SITE_INFORMATION_NOTES_SUBSECTION_FIELDS}
            headerValues={headerValues}
          />
          <FormInfoHelpModal
            visible={infoKey !== null}
            onClose={() => setInfoKey(null)}
            title={
              infoKey != null ? SITE_INFO_STEP_FIELD_INFO[infoKey].title : ''
            }
            bullets={
              infoKey != null
                ? [...SITE_INFO_STEP_FIELD_INFO[infoKey].bullets]
                : []
            }
          />
        </FormStackColumnsOnWebProvider>
      </FormFieldShowInfoIconsProvider>
    </View>
  );
};

const siteInformationInitialStyles = StyleSheet.create({
  opportunityFormSection: {
    marginBottom: spacing.pdStepSubsectionGap,
    borderColor: colors.borderCard,
  },
  /** Label + switch on one row; vertical spacing comes from `FormFieldStack` gap only. */
  raisedFloorChoiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    columnGap: spacing.sm,
    rowGap: spacing.xs,
    ...Platform.select({
      web: {
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
      },
      default: {},
    }),
  },
  raisedFloorRadios: {
    flexShrink: 0,
    marginBottom: 0,
  },
});

const siteInformationPersistedStyles = StyleSheet.create({
  container: {
    width: '100%',
  },
  rootWeb: {
    maxWidth: spacing.formMaxWidth,
    alignSelf: 'center',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
  pdfButtonContainer: {},
  pageTitle: {
    fontSize: fontSize.formStepPageTitle,
    lineHeight: lineHeight.formStepPageTitle,
    fontWeight: fontWeight.bold,
    color: colors.primary,
    marginBottom: spacing.belowFormStepPageTitle,
  },
  opportunityFormSection: {
    marginBottom: spacing.pdStepSubsectionGap,
    borderColor: colors.borderCard,
  },
  charCount: {
    fontSize: 12,
    color: '#888',
    textAlign: 'right',
    marginTop: 4,
  },
  raisedFloorChoiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    columnGap: spacing.sm,
    rowGap: spacing.xs,
    ...Platform.select({
      web: {
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
      },
      default: {},
    }),
  },
  raisedFloorRadios: {
    flexShrink: 0,
    marginBottom: 0,
  },
});

export const SiteInformation: React.FC<SiteInformationProps> = props =>
  isSiteInformationPersistedProps(props) ? (
    <SiteInformationPersistedLayout {...props} />
  ) : (
    <SiteInformationInitialLayout {...props} />
  );

SiteInformation.displayName = 'SiteInformation';

/** @deprecated Test / caption suites — compose path only */
export const SiteDimensionsStep = SiteInformationInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const SiteDimensionsInRequest = SiteInformationPersistedLayout;
